import React, { useEffect, useRef, useState } from 'react';
import {
  MessageSquare,
  Newspaper,
  TrendingUp,
  Clock,
  ExternalLink,
  ChevronRight,
  Eye,
  MessageCircle,
  Calendar,
  Briefcase,
  MapPin,
  DollarSign
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useSocket } from "../../contexts/SocketContext";

const RightSidebar = () => {
  const navigate = useNavigate();
  const socket = useSocket();
  const socketRef = useRef(socket);

  const [onlineCount, setOnlineCount] = useState(0);
  const [latestNews, setLatestNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    socketRef.current.connect();

    socketRef.current.on("connect", () => {
      console.log("Connected to socket:", socketRef.current.id);
    });

    socket.on("online-users", list => setOnlineCount(list?.length));

    // Fetch latest news
    fetchLatestNews();

    return () => {
      socketRef.current?.off("online-users");
      socketRef.current?.disconnect();
    };
  }, []);

  const fetchLatestNews = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/news/all?status=published&limit=4&sortBy=publishedAt&sortOrder=desc`);
      const data = await response.json();

      if (data.success) {
        setLatestNews(data.news || []);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate time ago
  const getTimeAgo = (date) => {
    const now = new Date();
    const posted = new Date(date);
    const diffInHours = Math.floor((now - posted) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h`;
    if (diffInHours < 48) return '1d';
    return `${Math.floor(diffInHours / 24)}d`;
  };

  // Get category color
  const getCategoryColor = (category) => {
    const colors = {
      'achievements': 'bg-green-100 text-green-700',
      'events': 'bg-blue-100 text-blue-700',
      'careers': 'bg-purple-100 text-purple-700',
      'academics': 'bg-indigo-100 text-indigo-700',
      'alumni-spotlight': 'bg-yellow-100 text-yellow-700',
      'research': 'bg-pink-100 text-pink-700',
      'announcements': 'bg-red-100 text-red-700',
      'community': 'bg-gray-100 text-gray-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  // Get category display name
  const getCategoryName = (category) => {
    const names = {
      'achievements': 'Achievement',
      'events': 'Event',
      'careers': 'Career',
      'academics': 'Academic',
      'alumni-spotlight': 'Spotlight',
      'research': 'Research',
      'announcements': 'News',
      'community': 'Community'
    };
    return names[category] || 'News';
  };

  const handleNewsClick = (news) => {
    navigate(`/home/news/${news._id}`);
  };

  // Dummy forum data (keeping the same as original)
  const forumTopics = [
    {
      id: 1,
      title: "Career Guidance for Fresh Graduates",
      category: "Career",
      replies: 24,
      lastActivity: "2h",
      isHot: true
    },
    {
      id: 2,
      title: "Alumni Meetup 2025 Planning",
      category: "Events",
      replies: 18,
      lastActivity: "4h",
      isHot: false
    },
    {
      id: 3,
      title: "Tech Industry Trends Discussion",
      category: "Technology",
      replies: 31,
      lastActivity: "6h",
      isHot: true
    },
    {
      id: 4,
      title: "Startup Funding Opportunities",
      category: "Business",
      replies: 12,
      lastActivity: "1d",
      isHot: false
    }
  ];

  return (
    <div className="space-y-4">
      {/* Online Users */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-gray-900">Online Now</span>
          </div>
          <div className=" dynamic-site-bg-3  text-white text-xs font-bold px-2 py-1 rounded-full">
            {onlineCount || 1}
          </div>
        </div>
      </div>

      {/* Latest News Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 ">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Newspaper className="w-4 h-4 text-blue-600" />
            </div>
            <h3 className="font-semibold text-gray-900">Latest News</h3>
          </div>
          <ChevronRight className="w-4 h-4 text-gray-400" />
        </div>

        <div className="p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#71be95]"></div>
            </div>
          ) : latestNews.length > 0 ? (
            latestNews.slice(0, 3).map((news) => (
              <div key={news._id} onClick={() => handleNewsClick(news)} className="group cursor-pointer">
                <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors border border-gray-100 overflow-hidden">
                  {news.featuredImage ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={news.featuredImage} 
                        alt={news.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Newspaper className="w-5 h-5 text-gray-600" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h4 className="font-medium text-gray-900 text-sm leading-tight line-clamp-2">
                          {news.title}
                        </h4>
                        {news.subtitle && (
                          <p className="text-xs text-gray-500 mt-1 line-clamp-1">
                            {news.subtitle}
                          </p>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 font-medium ${getCategoryColor(news.category)}`}>
                        {getCategoryName(news.category)}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>{getTimeAgo(news.publishedAt || news.createdAt)}</span>
                      </div>

                      {news.readTime && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          <span>{news.readTime}</span>
                        </div>
                      )}

                      {news.views > 0 && (
                        <div className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          <span>{news.views}</span>
                        </div>
                      )}
                    </div>

                    {news.priority === 'high' && (
                      <div className="mt-2">
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-md font-medium">
                          Breaking
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-6">
              <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No news available</p>
            </div>
          )}

          {!loading && latestNews.length > 0 && (
            <Link
              to="/home/news"
              className="block w-full text-center py-2 text-sm text-[#71be95] hover:text-[#5fa080] font-medium transition-colors"
            >
              View All News
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default RightSidebar;
